import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import styled from "styled-components";

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  thead th {
    position: sticky;
    top: 0;
    background-color: #f8f9fa;
    z-index: 1;
    padding: 10px;
    border-bottom: 2px solid #dee2e6;
    cursor: pointer;
    user-select: none;
  }
  tbody td {
    padding-top: 0px;
    padding-left: 10px;
    padding-right: 10px;
    border-bottom: 1px solid #dee2e6;
  }
`;

function toArray(val) {
  if (val == null) return [];
  return Array.isArray(val) ? val : [val];
}

// zip keys + dirs, filling dirs with first dir or "ascending" if shorter
function zipSort(keys, dirs) {
  if (!keys.length) return [];
  const baseDir = dirs[0] || "ascending";
  return keys.map((k, i) => ({
    key: k,
    direction: dirs[i] || baseDir,
  }));
}

export function SortableTable({
  data,
  // Uncontrolled defaults (used once on mount)
  defaultSortKey = null,                 // string | string[]
  defaultSortDirection = "ascending",    // "ascending" | "descending" | string[]
  // Controlled API (all optional)
  sortKey,                               // string | string[]
  sortDir,                               // "ascending" | "descending" | string[]
  onSortChange,                          // ({key,dir}) OR ({keys,dirs})
  // Behavior
  customSortFunctions = {},
  disableSortKeys = [],
  // Row identity
  rowKey, // string field name OR (row) => string
}) {
  const headers = useMemo(
    () => (Array.isArray(data) && data.length > 0 ? Object.keys(data[0]) : []),
    [data]
  );

  const [internalSortConfig, setInternalSortConfig] = useState([]);
  const didInitDefault = useRef(false);

  // Initialize default sort ONCE (supports string or array)
  useEffect(() => {
    if (didInitDefault.current) return;

    const dKeys = toArray(defaultSortKey).filter(
      (k) => k && data && data.length > 0 && data[0][k] !== undefined
    );
    const dDirs = toArray(defaultSortDirection);
    if (dKeys.length) {
      setInternalSortConfig(zipSort(dKeys, dDirs));
    }
    didInitDefault.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSortKey, defaultSortDirection]);

  // Build active sort config (controlled or internal)
  const activeSortConfig = useMemo(() => {
    const cKeys = toArray(sortKey);
    const cDirs = toArray(sortDir);
    if (cKeys.length && cDirs.length) return zipSort(cKeys, cDirs);
    if (cKeys.length) return zipSort(cKeys, ["ascending"]);
    return internalSortConfig;
  }, [sortKey, sortDir, internalSortConfig]);

  const handleSort = useCallback(
    (key) => {
      if (disableSortKeys.includes(key)) return;

      const isControlledSingle = typeof sortKey === "string";
      const isControlledMulti = Array.isArray(sortKey);

      if (onSortChange && (isControlledSingle || isControlledMulti)) {
        // Controlled mode
        if (isControlledSingle) {
          const nextDir =
            sortKey === key ? (sortDir === "ascending" ? "descending" : "ascending") : "ascending";
          // single-key callback (back-compat)
          onSortChange({ key, dir: nextDir });
          return;
        }

        // Controlled multi-key
        const keys = toArray(sortKey);
        const dirs = toArray(sortDir);
        const curPrimaryKey = keys[0];
        const curPrimaryDir = dirs[0] || "ascending";

        let nextKeys = keys.slice();
        let nextDirs = dirs.slice();

        const existingIdx = nextKeys.indexOf(key);
        if (existingIdx === 0) {
          // toggle primary
          nextDirs[0] = curPrimaryDir === "ascending" ? "descending" : "ascending";
        } else {
          // promote clicked key to primary with ascending; remove any duplicate further down
          if (existingIdx > -1) {
            nextKeys.splice(existingIdx, 1);
            nextDirs.splice(existingIdx, 1);
          }
          nextKeys = [key, ...nextKeys];
          nextDirs = ["ascending", ...nextDirs];
        }

        // multi-key callback shape
        onSortChange({ keys: nextKeys, dirs: nextDirs });
        return;
      }

      // Uncontrolled: maintain multi-sort (most recent first)
      setInternalSortConfig((prev) => {
        const idx = prev.findIndex((c) => c.key === key);
        if (idx > -1) {
          const current = prev[idx];
          const toggled = {
            key,
            direction: current.direction === "ascending" ? "descending" : "ascending",
          };
          const next = [...prev];
          next.splice(idx, 1);
          return [toggled, ...next];
        }
        return [{ key, direction: "ascending" }, ...prev];
      });
    },
    [disableSortKeys, onSortChange, sortKey, sortDir]
  );

  const getSortIcon = (key) => {
    if (!activeSortConfig.length || activeSortConfig[0].key !== key) return <FaSort />;
    return activeSortConfig[0].direction === "ascending" ? <FaSortUp /> : <FaSortDown />;
  };

  const compareValues = (key, aRow, bRow) => {
    const custom = customSortFunctions[key];
    const aVal = aRow[key];
    const bVal = bRow[key];

    if (typeof custom === "function") {
      try {
        const r = custom(aVal, bVal);
        return typeof r === "number" ? r : 0;
      } catch {
        return 0;
      }
    }

    if (aVal === bVal) return 0;

    // Numeric compare when possible
    const aNum = Number(aVal);
    const bNum = Number(bVal);
    const aNumOK = !Number.isNaN(aNum);
    const bNumOK = !Number.isNaN(bNum);
    if (aNumOK && bNumOK) return aNum < bNum ? -1 : 1;

    // String compare
    if (typeof aVal === "string" && typeof bVal === "string") {
      return aVal < bVal ? -1 : 1;
    }

    // Date-ish strings
    const aDate = new Date(aVal);
    const bDate = new Date(bVal);
    if (!Number.isNaN(aDate.getTime()) && !Number.isNaN(bDate.getTime())) {
      const da = aDate.getTime();
      const db = bDate.getTime();
      if (da === db) return 0;
      return da < db ? -1 : 1;
    }

    // React nodes/objects → unsortable
    return 0;
  };

  const sortedData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    if (!activeSortConfig.length) return data; // keep original order stable

    const copy = [...data];
    copy.sort((a, b) => {
      for (const { key, direction } of activeSortConfig) {
        const result = compareValues(key, a, b);
        if (result !== 0) return direction === "ascending" ? result : -result;
      }
      return 0;
    });
    return copy;
  }, [data, activeSortConfig, customSortFunctions]);

  const getRowKey = (row, index) => {
    if (typeof rowKey === "function") return String(rowKey(row));
    if (typeof rowKey === "string" && row && row[rowKey] != null) return String(row[rowKey]);
    return String(index); // fallback
  };

  return (
    <StyledTable>
      <thead>
        <tr>
          {headers.map((key) => (
            <th
              key={key}
              onClick={() => handleSort(key)}
              style={{ cursor: disableSortKeys.includes(key) ? "default" : "pointer" }}
            >
              {key} {getSortIcon(key)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedData.map((item, index) => (
          <tr key={getRowKey(item, index)}>
            {headers.map((key) => (
              <td key={key}>{item[key]}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </StyledTable>
  );
}

export default SortableTable;